// src/components/DocumentReviewQueue.tsx
// Queue of documents pending review, priority-sorted.

import { useMemo } from 'react';
import { useI18n } from '@react/hooks';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  ErrorAlert,
} from '@react/components';

import {
  getEntityId,
  getDocumentType,
  getDocumentOwner,
  getStatusMapping,
  documentTypeMap,
  formatDate,
  getReviewPriority,
} from '@/features/document/logic';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DocumentReviewQueueProps {
  documents: any[];
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onRequestChanges: (id: string) => void;
  error?: string | null;
  isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Priority config
// ---------------------------------------------------------------------------

const PRIORITY_CONFIG: Record<string, { variant: string; labelKey: string; order: number }> = {
  high: { variant: 'error', labelKey: 'document.review.priorityHigh', order: 0 },
  medium: { variant: 'warning', labelKey: 'document.review.priorityMedium', order: 1 },
  low: { variant: 'secondary', labelKey: 'document.review.priorityLow', order: 2 },
};

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function ReviewQueueSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="rounded-lg border border-border p-4 animate-pulse">
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              <div className="h-4 w-3/4 rounded bg-muted" />
              <div className="flex gap-2">
                <div className="h-5 w-16 rounded-full bg-muted" />
                <div className="h-5 w-20 rounded-full bg-muted" />
              </div>
              <div className="h-3 w-1/2 rounded bg-muted" />
            </div>
            <div className="flex gap-2">
              <div className="h-8 w-20 rounded bg-muted" />
              <div className="h-8 w-20 rounded bg-muted" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentReviewQueue({
  documents,
  onApprove,
  onReject,
  onRequestChanges,
  error,
  isLoading,
}: DocumentReviewQueueProps) {
  const { t, locale } = useI18n();

  // Sort documents by priority (high first)
  const sortedDocs = useMemo(() => {
    return [...documents].sort((a, b) => {
      const pa = PRIORITY_CONFIG[getReviewPriority(a)]?.order ?? 2;
      const pb = PRIORITY_CONFIG[getReviewPriority(b)]?.order ?? 2;
      return pa - pb;
    });
  }, [documents]);

  if (error) {
    return <ErrorAlert error={error} />;
  }

  if (isLoading) {
    return <ReviewQueueSkeleton />;
  }

  if (sortedDocs.length === 0) {
    return (
      <div className="py-16 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-lg font-medium text-foreground">{t('document.review.emptyTitle')}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t('document.review.emptyDescription')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sortedDocs.map((doc: any) => {
        const id = getEntityId(doc);
        const docType = getDocumentType(doc);
        const owner = getDocumentOwner(doc);
        const typeInfo = getStatusMapping(documentTypeMap, docType);
        const priority = getReviewPriority(doc);
        const priorityInfo = PRIORITY_CONFIG[priority];
        const submittedAt = doc?.submitted_at || doc?.updated_at;

        // Calculate days in review
        const daysInReview = submittedAt
          ? Math.max(0, Math.floor((Date.now() - new Date(submittedAt).getTime()) / (1000 * 60 * 60 * 24)))
          : 0;

        return (
          <Card key={id} className="transition-colors hover:border-primary/20">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-sm font-semibold text-foreground truncate">
                    {doc.title || t('common.untitled')}
                  </CardTitle>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <Badge variant={typeInfo.variant as any} size="sm">
                      {t(typeInfo.labelKey)}
                    </Badge>
                    <Badge variant={priorityInfo.variant as any} size="sm">
                      {t(priorityInfo.labelKey)}
                    </Badge>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => onRequestChanges(id)}>
                    {t('document.review.requestChanges')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onReject(id)}>
                    {t('document.review.reject')}
                  </Button>
                  <Button size="sm" onClick={() => onApprove(id)}>
                    {t('document.review.approve')}
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                {owner && (
                  <span>
                    {t('document.review.submittedBy')}: {owner}
                  </span>
                )}
                {submittedAt && (
                  <span>
                    {t('document.review.submittedDate')}: {formatDate(submittedAt, locale)}
                  </span>
                )}
                <span>
                  {t('document.review.daysInReview')}: {daysInReview}
                </span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default DocumentReviewQueue;
